import React, { useState, useEffect, useMemo } from "react";
import {
    Layout, Breadcrumb, Table, Button, Divider, Form, Input,
    Row, Col, Space, Popconfirm, Typography, Avatar, Select, message, Tag
} from "antd";
import {
    HomeOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
    SearchOutlined, RedoOutlined
} from "@ant-design/icons";
import moment from "moment";
import { Link, useHistory, useLocation } from "react-router-dom";

import * as Service from "../../../config/service";
import CONSTANT from "../../../config/constant";

const ServiceList = () => {
    const history = useHistory();
    const location = useLocation();
    const [form] = Form.useForm();

    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [agencyServices, setAgencyServices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    // Lấy query params từ URL
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const [pagination, setPagination] = useState({
        current: Number(query.get("page")) || 1,
        pageSize: Number(query.get("pageSize")) || 10,
        total: 0
    });

    const searchOptions = useMemo(() => {
        let opts = {};
        const q = query.get("q");
        const field = query.get("field");
        const created_by = query.get("created_by");

        if (q && field) opts[field] = q;
        if (created_by) opts.created_by = created_by;

        return opts;
    }, [query]);

    const sortValue = useMemo(() => {
        return query.get("sort") || `{"created_at": -1}`;
    }, [query]);

    // Columns
    const columns = [
        {
            title: "Ảnh",
            dataIndex: "images",
            width: 50,
            render: imgs =>
                imgs?.length > 0 && (
                    <Avatar
                        size={48}
                        shape="square"
                        src={`${CONSTANT.STATIC_URL}${imgs[0].filename}`}
                    />
                )
        },
        { title: "Tên", dataIndex: "name" },
        {
            title: "Người dùng kích hoạt",
            dataIndex: "status",
            width: 120,
            render: st => (
                <>
                    {st === "publish" && <Tag color="green">Công khai</Tag>}
                    {st === "draft" && <Tag color="orange">Bản nháp</Tag>}
                </>
            )
        },
        {
            title: "Kiểm duyệt",
            dataIndex: "is_active",
            width: 120,
            render: active => (
                <Tag color={active ? "green" : "red"}>
                    {active ? "Đã kiểm duyệt" : "Chưa kiểm duyệt"}
                </Tag>
            )
        },
        {
            title: "Ngày tạo",
            dataIndex: "created_at",
            sorter: true,
            width: 170,
            render: d => moment(d).format(CONSTANT.DATE_FORMAT)
        },
        {
            title: "Hành động",
            width: 180,
            render: (text, record) => (
                <Space>
                    <Link to={`/service/action/${record._id}`}>
                        <EditOutlined /> Sửa
                    </Link>
                    <Divider type="vertical" />
                    <Popconfirm
                        title="Bạn có chắc xóa?"
                        onConfirm={() => handleDelete(record._id)}
                    >
                        <a><DeleteOutlined /> Xóa</a>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    // Hàm cập nhật params lên URL
    const updateParams = (newParams = {}) => {
        const params = new URLSearchParams(location.search);

        Object.keys(newParams).forEach(key => {
            if (newParams[key] === "" || newParams[key] == null) params.delete(key);
            else params.set(key, newParams[key]);
        });

        history.push({ search: params.toString() });
    };

    // Fetch data
    const fetchData = async () => {
        setLoading(true);

        await Service.get(CONSTANT.API.SERVICE.SERVICE, {
            perPage: pagination.pageSize,
            page: pagination.current,
            sort: sortValue,
            ...searchOptions
        })
            .then(res => {
                setData(res.data?.data?.docs || []);
                setPagination(prev => ({
                    ...prev,
                    total: res.data?.data?.total || 0   // <-- total nằm trong pagination
                }));
            })
            .finally(() => setLoading(false));
    };

    // Load Agencies
    const loadAgencies = async () => {
        const res = await Service.get(`${CONSTANT.API.USER.USER}?type=service&perPage=-1`);
        if (res?.data?.code === 200) setAgencyServices(res.data.data.docs);
    };

    useEffect(() => {
        loadAgencies();
    }, []);

    useEffect(() => {
        fetchData();
    }, [location.search]);

    // Delete one
    const handleDelete = async id => {
        const res = await Service.remove(`${CONSTANT.API.SERVICE.SERVICE}/${id}`);
        if (res?.data?.code === 200) {
            message.success("Xóa thành công");
            fetchData();
        }
    };

    const handleTableChange = (pag) => {
        setPagination(prev => ({
            ...prev,
            current: pag.current,
            pageSize: pag.pageSize
        }));
    };
    // Search submit
    const onSearch = values => {
        updateParams({
            q: values.q || "",
            field: values.field || "name",
            created_by: values.created_by || "",
            page: 1
        });
    };

    const reload = () => {
        form.resetFields();
        history.push({ search: "" });
    };

    // Delete selected
    const removeSelected = async () => {
        let count = 0;

        for (let id of selectedRowKeys) {
            const res = await Service.remove(`${CONSTANT.API.SERVICE.SERVICE}/${id}`);
            if (res?.data?.code === 200) count++;
        }

        message.success(`Đã xóa ${count} dòng`);
        setSelectedRowKeys([]);
        fetchData();
    };

    return (
        <div>
            <Breadcrumb style={{ margin: "16px 0" }}>
                <Breadcrumb.Item>
                    <Link to="/"><HomeOutlined /></Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>Dịch vụ</Breadcrumb.Item>
            </Breadcrumb>

            <Layout.Content className="layout-content">
                {/* Search Form */}
                <Form
                    form={form}
                    layout="inline"
                    onFinish={onSearch}
                    initialValues={{
                        q: query.get("q") || "",
                        field: query.get("field") || "name",
                        created_by: query.get("created_by") || ""
                    }}
                    style={{ marginBottom: 20 }}
                >
                    <Form.Item name="q">
                        <Input placeholder="Nhập tên hoặc Id" style={{ width: 300 }} />
                    </Form.Item>

                    <Form.Item name="field">
                        <Select style={{ width: 150 }}>
                            <Select.Option value="name">Tên</Select.Option>
                            <Select.Option value="uid">Id</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name="created_by">
                        <Select
                            allowClear
                            showSearch
                            placeholder="Nhà cung cấp dịch vụ"
                            style={{ minWidth: 200 }}
                        >
                            {agencyServices.map(item => (
                                <Select.Option key={item._id} value={item._id}>
                                    {item.firstname} {item.lastname} – {item.email}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                            Tìm
                        </Button>
                    </Form.Item>

                    <Form.Item>
                        <Button onClick={reload} icon={<RedoOutlined />}>
                            Đặt lại
                        </Button>
                    </Form.Item>
                </Form>

                <Divider />

                <Row justify="space-between" style={{ marginBottom: 20 }}>
                    <Col>
                        <Popconfirm
                            title="Bạn có chắc xóa?"
                            onConfirm={removeSelected}
                        >
                            <Button
                                type="primary"
                                icon={<DeleteOutlined />}
                                disabled={!selectedRowKeys.length}
                            >
                                Xóa
                            </Button>
                        </Popconfirm>

                        {selectedRowKeys.length > 0 && (
                            <Typography.Text type="secondary" style={{ marginLeft: 10 }}>
                                Đã chọn {selectedRowKeys.length} dòng
                            </Typography.Text>
                        )}
                    </Col>

                    <Col>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => history.push("/service/action")}
                        >
                            Thêm mới
                        </Button>
                    </Col>
                </Row>

                <Table
                    rowKey={r => r._id}
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    rowSelection={{
                        selectedRowKeys,
                        onChange: setSelectedRowKeys
                    }}
                    pagination={pagination}
                    onChange={handleTableChange}
                />
            </Layout.Content>
        </div>
    );
};

export default ServiceList;
